import { Link, useLocation } from 'react-router-dom';
import { Headphones, PhoneCall } from 'lucide-react';
import { useCentralita } from '@/hooks/useCentralita';
import { soportaCentralita } from './AnclaCCP';

/**
 * El acceso a la centralita, en el menú lateral.
 *
 * Va en verde y separado del resto del menú a propósito. Todo lo demás del CRM
 * es consulta —listados, fichas, informes—; esto es lo único que hace algo en
 * el mundo real: descuelga un teléfono. Que se distinga de un vistazo evita
 * pulsarlo sin querer y, sobre todo, permite encontrarlo deprisa cuando hay que
 * llamar a alguien.
 *
 * Además hace de piloto: si hay una llamada en curso, late. Como el panel solo
 * se ve en su pantalla y la llamada sobrevive a la navegación, este botón es lo
 * que recuerda desde cualquier parte del CRM que la línea sigue abierta.
 */
export default function BotonCentralita({ onNavegar }: { onNavegar?: () => void }) {
  const { estado, numero, destino } = useCentralita();
  const activo = useLocation().pathname.startsWith('/centralita');

  if (!soportaCentralita()) return null;

  const enLlamada = estado === 'marcando' || estado === 'sonando' || estado === 'hablando';
  const enAcw = estado === 'tras-llamada';

  const rotulo =
    estado === 'sonando' ? 'Llamada entrante'
    : estado === 'marcando' ? 'Llamando…'
    : estado === 'hablando' ? (destino?.nombre ?? numero ?? 'En llamada')
    : enAcw ? 'Terminar la llamada'
    : 'Centralita';

  return (
    <Link
      to="/centralita"
      onClick={onNavegar}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors w-full
        ${enLlamada
          ? 'bg-emerald-500 text-white hover:bg-emerald-400'
          : activo
            ? 'bg-emerald-600 text-white'
            : 'bg-emerald-600/90 text-white hover:bg-emerald-500'}`}
    >
      {enLlamada ? (
        <PhoneCall size={18} className="flex-shrink-0 animate-pulse" />
      ) : (
        <Headphones size={18} className="flex-shrink-0" />
      )}
      <span className="flex-1 text-left truncate">{rotulo}</span>
      {/* El punto solo aparece cuando hay algo que atender, para que su ausencia
          signifique «no pasa nada» sin tener que leer. */}
      {(enLlamada || enAcw) && (
        <span className={`h-2 w-2 rounded-full shrink-0 ${enAcw ? 'bg-white/70' : 'bg-white animate-pulse'}`} />
      )}
    </Link>
  );
}
